/**
 * widgetSources.js — Deklarative Datenquellen-Registry für das generische
 * Dashboard-Widget-System.
 *
 * Jede Quelle beschreibt:
 *  - welche Visualisierungen sie unterstützt (`supports`)
 *  - welche Parameter live einstellbar sind (`params`: days / limit)
 *  - Dimensionen (für Donut/Tabelle) bzw. Felder (für KPI/Summary)
 *  - Adapter-Funktionen, die die API-Response in das Format des jeweiligen
 *    Viz-Renderers übersetzen. Adapter erhalten ein `ctx`-Objekt
 *    ({ t, lang, countryLabel }) für i18n-abhängige Labels.
 *
 * Labels liegen bilingual inline ({ de, en }) — Auflösung via `wl(label, lang)`.
 */

import { countryFromDetails } from '@/utils/country'

export const GRID_COLS   = 24
export const GRID_ROW_PX = 40
export const GRID_MARGIN = 8

// ── Label-Helper ──────────────────────────────────────────────────────────────
export function wl(label, lang = 'de') {
  if (label == null) return ''
  if (typeof label === 'string') return label
  return label[lang] ?? label.de ?? label.en ?? ''
}

// ── Viz-Typen ────────────────────────────────────────────────────────────────
export const VIZ_TYPES = [
  { key: 'kpi',         icon: 'pi pi-hashtag',     label: { de: 'Zahl (KPI)',        en: 'Number (KPI)' },
    desc: { de: 'Einzelne Kennzahl mit Akzentfarbe', en: 'Single key figure with accent color' } },
  { key: 'summary',     icon: 'pi pi-list-check',  label: { de: 'Summary-Block',     en: 'Summary block' },
    desc: { de: 'Mehrere Kennzahlen einer Quelle nebeneinander', en: 'Multiple key figures of one source' } },
  { key: 'table',       icon: 'pi pi-table',       label: { de: 'Tabelle',           en: 'Table' },
    desc: { de: 'Sortierbare Datentabelle', en: 'Sortable data table' } },
  { key: 'donut',       icon: 'pi pi-chart-pie',   label: { de: 'Donut',             en: 'Donut' },
    desc: { de: 'Verteilung als Donut-Chart', en: 'Distribution as donut chart' } },
  { key: 'donut-table', icon: 'pi pi-percentage',  label: { de: 'Donut + Liste',     en: 'Donut + list' },
    desc: { de: 'Donut mit Top-Liste daneben', en: 'Donut with ranked list beside it' } },
  { key: 'line',        icon: 'pi pi-chart-line',  label: { de: 'Linechart',         en: 'Line chart' },
    desc: { de: 'Zeitreihe als Liniendiagramm', en: 'Time series as line chart' } },
  { key: 'map',         icon: 'pi pi-map',         label: { de: 'World Map',         en: 'World map' },
    desc: { de: 'Choropleth-Weltkarte', en: 'Choropleth world map' } },
  { key: 'feed',        icon: 'pi pi-history',     label: { de: 'Activity-Feed',     en: 'Activity feed' },
    desc: { de: 'Chronologische Aktivitätsliste', en: 'Chronological activity list' } },
  { key: 'info',        icon: 'pi pi-info-circle', label: { de: 'Info / Willkommen', en: 'Info / welcome' },
    desc: { de: 'Statisches Info-Widget (Drei-Säulen-Einführung)', en: 'Static info widget (three pillars intro)' } },
]

export const vizMeta = (key) => VIZ_TYPES.find(v => v.key === key) ?? null

// ── Standard-Adapter-Helfer ──────────────────────────────────────────────────
const unwrap = (res) => res?.data?.data ?? res?.data ?? res ?? {}

// [{id?, name, count}] → Donut-Slices
function listToSlices(list, ctx, { country = false, mapLabel = null } = {}) {
  return (list ?? []).map(r => ({
    label: mapLabel ? mapLabel(r, ctx)
         : country  ? (r.name ? ctx.countryLabel(r.name) : '—')
         : (r.name ?? '—'),
    value: Number(r.count ?? 0),
    raw:   r.id ?? r.name ?? null,
  }))
}

// {key: number} → Donut-Slices mit Label-Map
function objToSlices(obj, labels, ctx) {
  return Object.entries(obj ?? {}).map(([k, v]) => ({
    label: wl(labels?.[k], ctx.lang) || k,
    value: Number(v ?? 0),
    raw:   k,
  }))
}

// Slices → einfache Name/Anzahl-Tabellenzeilen
const slicesToRows = (slices) => slices.map((s, i) => ({ id: s.raw ?? i, name: s.label, count: s.value }))

const NAME_COUNT_COLUMNS = [
  { field: 'name',  label: { de: 'Name',   en: 'Name'  }, sortable: true },
  { field: 'count', label: { de: 'Anzahl', en: 'Count' }, sortable: true, align: 'right' },
]

// Stats-Quelle (Dimensionen → Donut/Donut-Liste/Tabelle) als Fabrik
function statsSource(def) {
  return {
    supports: ['donut', 'donut-table', 'table'],
    defaultViz: 'donut',
    columns: () => NAME_COUNT_COLUMNS,
    rows: (res, cfg, ctx) => slicesToRows(def.slices(res, cfg, ctx)),
    ...def,
  }
}

// ── Quellen-Registry ─────────────────────────────────────────────────────────
export const WIDGET_SOURCES = {

  // ════ Threat News ══════════════════════════════════════════════════════════
  'posts-stats': statsSource({
    label:  { de: 'Ransomware-Posts – Statistik', en: 'Ransomware posts – statistics' },
    pillar: 'threatnews',
    api:    'getPostsStats',
    params: { days: true },
    dimensions: {
      groups:     { label: { de: 'Top Gruppen',  en: 'Top groups' } },
      countries:  { label: { de: 'Top Länder',   en: 'Top countries' }, country: true },
      industries: { label: { de: 'Branchen',     en: 'Industries' } },
    },
    slices(res, cfg, ctx) {
      const d = unwrap(res)
      const dim = cfg.dimension ?? 'groups'
      return listToSlices(d[dim], ctx, { country: dim === 'countries' })
    },
    link: '/threat-news/posts',
  }),

  'posts-summary': {
    label:  { de: 'Ransomware-Posts – Summary', en: 'Ransomware posts – summary' },
    pillar: 'threatnews',
    api:    'getHackPostsSummary',
    supports: ['kpi', 'summary'],
    defaultViz: 'kpi',
    fields: {
      total_posts:      { label: { de: 'Gesamt',         en: 'Total' },        variant: 'primary', icon: 'file' },
      posts_last_day:   { label: { de: 'Letzte 24h',     en: 'Last 24h' },     variant: 'warning', icon: 'sun' },
      posts_last_week:  { label: { de: 'Letzte 7 Tage',  en: 'Last 7 days' },  variant: 'teal',    icon: 'calendar' },
      posts_last_month: { label: { de: 'Letzte 30 Tage', en: 'Last 30 days' }, variant: 'info',    icon: 'clock' },
    },
    kpi: (res, field) => Number(unwrap(res)?.[field] ?? 0),
    link: '/threat-news/posts',
  },

  'posts-latest': {
    label:  { de: 'Neueste Ransomware-Posts', en: 'Latest ransomware posts' },
    pillar: 'threatnews',
    api:    'getHackPostsFiltered',
    params: { limit: true },
    supports: ['table'],
    defaultViz: 'table',
    apiParams: (cfg) => ({ limit: cfg.limit ?? 10, page: 1 }),
    columns: () => [
      { field: '_country',          label: { de: 'Land',     en: 'Country' },    type: 'country' },
      { field: 'post_title',        label: { de: 'Titel',    en: 'Title' },      type: 'link', linkTo: r => `/threat-news/posts/${r.post_id}` },
      { field: 'hacker_group_name', label: { de: 'Gruppe',   en: 'Group' },      type: 'link', linkTo: r => `/threat-intel/actors/${r.hacker_groups_id}` },
      { field: 'discovered',        label: { de: 'Entdeckt', en: 'Discovered' }, type: 'date' },
    ],
    rows(res) {
      const d = res?.data ?? {}
      const list = d.rows ?? d.posts ?? (Array.isArray(d) ? d : [])
      // Ländercode steckt in details[] (setting_name 'country_code') — wie in
      // den Post-Tabellen via countryFromDetails zum {code, svgUrl}-Objekt auflösen
      return list.map(r => ({ ...r, _country: countryFromDetails(r.details) }))
    },
    link: '/threat-news/posts',
  },

  'posts-countries-map': {
    label:  { de: 'Ransomware-Posts – Weltkarte', en: 'Ransomware posts – world map' },
    pillar: 'threatnews',
    api:    'getHackPostsCountrySummary',
    supports: ['map'],
    defaultViz: 'map',
    mapValues(res, cfg, ctx) {
      const result = {}
      ;(res?.data ?? []).forEach(item => {
        const code = ctx.normalizeISO2(item.country)
        if (code) result[code] = Number(item.hack_count)
      })
      return result
    },
    mapUnit: { de: 'Posts', en: 'posts' },
    link: '/threat-news/posts',
  },

  'news-stats': statsSource({
    label:  { de: 'News – Statistik', en: 'News – statistics' },
    pillar: 'threatnews',
    api:    'getNewsStats',
    params: { days: true },
    dimensions: {
      countries: { label: { de: 'Top Länder', en: 'Top countries' }, country: true },
      sources:   { label: { de: 'Top Quellen', en: 'Top sources' } },
    },
    slices(res, cfg, ctx) {
      const d = unwrap(res)
      const dim = cfg.dimension ?? 'countries'
      return listToSlices(d[dim], ctx, { country: dim === 'countries' })
    },
    link: '/threat-news/news',
  }),

  'news-latest': {
    label:  { de: 'Neueste News', en: 'Latest news' },
    pillar: 'threatnews',
    api:    'getRssFeedItems',
    params: { limit: true },
    supports: ['table'],
    defaultViz: 'table',
    apiParams: (cfg) => ({ limit: cfg.limit ?? 10, page: 1 }),
    columns: () => [
      // Quelle nur als Symbol mit Tooltip — der Titel steht im Vordergrund
      { field: 'creator',      label: { de: '',       en: '' },        type: 'icon', icon: 'pi pi-globe' },
      { field: 'title',        label: { de: 'Titel',  en: 'Title' },   type: 'link', linkTo: r => `/threat-news/news/${r.id}` },
      { field: 'country_code', label: { de: 'Land',   en: 'Country' }, type: 'countryCode' },
      { field: 'pub_date',     label: { de: 'Datum',  en: 'Date' },    type: 'date' },
    ],
    rows(res) {
      const d = res?.data ?? {}
      return d.items ?? (Array.isArray(d) ? d : [])
    },
    link: '/threat-news/news',
  },

  // ════ Threat Intelligence ══════════════════════════════════════════════════
  'groups-summary': {
    label:  { de: 'Threat Actors – Summary', en: 'Threat actors – summary' },
    pillar: 'threatintel',
    api:    'getHackerGroupsSummary',
    supports: ['kpi', 'summary'],
    defaultViz: 'kpi',
    fields: {
      total_hacker_groups: { label: { de: 'Gruppen',    en: 'Groups' },    variant: 'primary', icon: 'users' },
      total_members:       { label: { de: 'Mitglieder', en: 'Members' },   variant: 'info',    icon: 'user' },
      total_locations:     { label: { de: 'Standorte',  en: 'Locations' }, variant: 'teal',    icon: 'map-marker' },
      total_profiles:      { label: { de: 'Profile',    en: 'Profiles' },  variant: 'purple',  icon: 'id-card' },
    },
    kpi: (res, field) => Number(unwrap(res)?.[field] ?? 0),
    link: '/threat-intel/actors',
  },

  'groups-stats': statsSource({
    label:  { de: 'Threat Actors – Statistik', en: 'Threat actors – statistics' },
    pillar: 'threatintel',
    api:    'getGroupsStats',
    params: { days: true },
    dimensions: {
      activity:  { label: { de: 'Aktivität',   en: 'Activity' } },
      countries: { label: { de: 'Top Länder',  en: 'Top countries' }, country: true },
      ttps:      { label: { de: 'Top TTPs',    en: 'Top TTPs' } },
      tools:     { label: { de: 'Top Tools',   en: 'Top tools' } },
      years:     { label: { de: 'Aktiv seit (Jahr)', en: 'Active since (year)' } },
    },
    slices(res, cfg, ctx) {
      const d   = unwrap(res)
      const dim = cfg.dimension ?? 'activity'
      if (dim === 'activity') {
        return objToSlices(
          { active: d.activity?.active, inactive: d.activity?.inactive },
          { active: { de: 'Aktiv', en: 'Active' }, inactive: { de: 'Inaktiv', en: 'Inactive' } },
          ctx
        )
      }
      return listToSlices(d[dim], ctx, { country: dim === 'countries' })
    },
    link: '/threat-intel/actors',
  }),

  'groups-hacked-7d': {
    label:  { de: 'Gruppen-Aktivität (gehackte Firmen)', en: 'Group activity (hacked companies)' },
    pillar: 'threatintel',
    api:    'getHackedCompaniesByGroup',
    params: { days: true },
    supports: ['table', 'donut', 'donut-table'],
    defaultViz: 'table',
    apiParams: (cfg) => ({ days: cfg.days ?? 7 }),
    columns: () => [
      { field: 'hacker_group_name', label: { de: 'Hacker-Gruppe', en: 'Hacker group' }, type: 'link', linkTo: r => `/threat-intel/actors/${r.hacker_groups_id}`, sortable: true },
      { field: 'hacked_count',      label: { de: 'Gehackte Firmen', en: 'Companies hacked' }, sortable: true, align: 'right' },
    ],
    rows: (res) => res?.data ?? [],
    slices: (res) => (res?.data ?? []).map(r => ({
      label: r.hacker_group_name, value: Number(r.hacked_count ?? 0), raw: r.hacker_groups_id,
    })),
    link: '/threat-intel/actors',
  },

  'tools-stats': statsSource({
    label:  { de: 'Angreifer-Tools – Statistik', en: 'Attacker tools – statistics' },
    pillar: 'threatintel',
    api:    'getToolsStats',
    dimensions: {
      categories: { label: { de: 'Kategorien', en: 'Categories' } },
      top_groups: { label: { de: 'Top Gruppen nach Tool-Anzahl', en: 'Top groups by tool count' } },
      top_tools:  { label: { de: 'Meistgenutzte Tools', en: 'Most used tools' } },
    },
    slices(res, cfg, ctx) {
      const d = unwrap(res)
      return listToSlices(d[cfg.dimension ?? 'categories'], ctx)
    },
    link: '/threat-intel/tools',
  }),

  'iocs-stats': statsSource({
    label:  { de: 'IOCs – Statistik', en: 'IOCs – statistics' },
    pillar: 'threatintel',
    api:    'getIOCsStats',
    dimensions: {
      groups:       { label: { de: 'Top Gruppen',     en: 'Top groups' } },
      types:        { label: { de: 'IOC-Typen',       en: 'IOC types' } },
      threat_types: { label: { de: 'Bedrohungstypen', en: 'Threat types' } },
      confidence:   { label: { de: 'Konfidenz',       en: 'Confidence' } },
    },
    slices(res, cfg, ctx) {
      const d   = unwrap(res)
      const dim = cfg.dimension ?? 'groups'
      if (dim === 'confidence') {
        const labels = {
          high:   { de: 'Hoch (≥75%)',     en: 'High (≥75%)' },
          medium: { de: 'Mittel (40–74%)', en: 'Medium (40–74%)' },
          low:    { de: 'Niedrig (<40%)',  en: 'Low (<40%)' },
        }
        return listToSlices(d.confidence, ctx, { mapLabel: (r) => wl(labels[r.name], ctx.lang) || r.name })
      }
      return listToSlices(d[dim], ctx)
    },
    link: '/threat-intel/iocs',
  }),

  'hackwatch-activity': {
    label:  { de: 'HackWatch – Recent Activity', en: 'HackWatch – recent activity' },
    pillar: 'threatintel',
    api:    'getHackerGroupsActivity',
    supports: ['feed'],
    defaultViz: 'feed',
    feed: (res, cfg, ctx) => (res?.data ?? []).map(item => ({
      title:  item.name,
      link:   `/threat-intel/actors/${item.hacker_groups_id}`,
      action: ctx.lang === 'de' ? 'Name geändert' : 'Name changed',
      ts:     item.ts,
    })),
    link: '/threat-intel/actors',
  },

  // ════ Detection ════════════════════════════════════════════════════════════
  'workflows-summary': {
    label:  { de: 'SIEM Workflows – Summary', en: 'SIEM workflows – summary' },
    pillar: 'detection',
    api:    'getWorkflowsSummary',
    supports: ['kpi', 'summary'],
    defaultViz: 'kpi',
    fields: {
      total_workflows:    { label: { de: 'Gesamt',   en: 'Total' },    variant: 'primary',  icon: 'desktop' },
      critical_workflows: { label: { de: 'Critical', en: 'Critical' }, variant: 'critical', icon: 'exclamation-circle',   link: '/detection/workflows?severity=CRITICAL' },
      high_workflows:     { label: { de: 'High',     en: 'High' },     variant: 'high',     icon: 'exclamation-triangle', link: '/detection/workflows?severity=HIGH' },
      medium_workflows:   { label: { de: 'Medium',   en: 'Medium' },   variant: 'medium',   icon: 'info-circle',          link: '/detection/workflows?severity=MEDIUM' },
      low_workflows:      { label: { de: 'Low',      en: 'Low' },      variant: 'success',  icon: 'minus-circle',         link: '/detection/workflows?severity=LOW' },
      info_workflows:     { label: { de: 'Info',     en: 'Info' },     variant: 'info',     icon: 'info-circle',          link: '/detection/workflows?severity=INFO' },
      featured_workflows: { label: { de: 'Featured', en: 'Featured' }, variant: 'info',     icon: 'star',                 link: '/detection/workflows?featured=true' },
    },
    kpi: (res, field) => Number(unwrap(res)?.[field] ?? 0),
    link: '/detection/workflows',
  },

  'workflows-stats': statsSource({
    label:  { de: 'SIEM Workflows – Statistik', en: 'SIEM workflows – statistics' },
    pillar: 'detection',
    api:    'getWorkflowsStats',
    dimensions: {
      severity:   { label: { de: 'Schweregrad',   en: 'Severity' } },
      visibility: { label: { de: 'Sichtbarkeit',  en: 'Visibility' } },
      mitre:      { label: { de: 'MITRE-Taktiken', en: 'MITRE tactics' } },
      isaca:      { label: { de: 'ISACA Use-Cases', en: 'ISACA use cases' } },
      tags:       { label: { de: 'Tags',           en: 'Tags' } },
    },
    slices(res, cfg, ctx) {
      const d = unwrap(res)
      return listToSlices(d[cfg.dimension ?? 'severity'], ctx)
    },
    link: '/detection/workflows',
  }),

  'workflow-changes': {
    label:  { de: 'SIEM Workflow-Änderungen (Verlauf)', en: 'SIEM workflow changes (history)' },
    pillar: 'detection',
    api:    'getWorkflowsChanges',
    supports: ['line'],
    defaultViz: 'line',
    series: (res) => (unwrap(res) ?? []).map?.(d => ({
      label: d.change_date, value: Number(d.changes),
    })) ?? [],
    seriesLabel: { de: 'Workflow-Änderungen', en: 'Workflow changes' },
    link: '/detection/workflows',
  },

  'siem-activity': {
    label:  { de: 'SIEM – Recent Activity', en: 'SIEM – recent activity' },
    pillar: 'detection',
    api:    'getWorkflowActivity',
    supports: ['feed'],
    defaultViz: 'feed',
    feed: (res, cfg, ctx) => (res?.data ?? []).map(item => ({
      kind:  item.action ?? null,   // 'workflow.create' | '.update' | '.delete' → Icon+Tooltip
      title: item.workflow_name
        ?? wl({ de: 'Gelöschter Workflow', en: 'Deleted workflow' }, ctx?.lang),
      link:  item.workflow_id != null && item.workflow_name
        ? `/detection/workflows/${item.workflow_id}` : null,
      user:  item.user_name || null,
      areas: Array.isArray(item.changed_fields) ? item.changed_fields : null,
      ts:    item.ts,
    })),
    link: '/detection/workflows',
  },

  'streams-stats': statsSource({
    label:  { de: 'SIEM Streams – Statistik', en: 'SIEM streams – statistics' },
    pillar: 'detection',
    api:    'getStreamsStats',
    dimensions: {
      types:         { label: { de: 'Stream-Typen',   en: 'Stream types' } },
      visibility:    { label: { de: 'Sichtbarkeit',   en: 'Visibility' } },
      categories:    { label: { de: 'Kategorien',     en: 'Categories' } },
      subcategories: { label: { de: 'Unterkategorien', en: 'Subcategories' } },
    },
    slices(res, cfg, ctx) {
      const d   = unwrap(res)
      const dim = cfg.dimension ?? 'types'
      if (dim === 'types') {
        return objToSlices(d.types, {
          managed: { de: 'Managed', en: 'Managed' },
          custom:  { de: 'Custom',  en: 'Custom' },
          wrapper: { de: 'Wrapper', en: 'Wrapper' },
        }, ctx)
      }
      if (dim === 'visibility') {
        return objToSlices(d.visibility, {
          community: { de: 'Community', en: 'Community' },
          company:   { de: 'Company',   en: 'Company' },
        }, ctx)
      }
      return listToSlices(d[dim], ctx)
    },
    link: '/detection/streams',
  }),

  // ════ Kunden / Partner ═════════════════════════════════════════════════════
  'customers-summary': {
    label:  { de: 'Kunden – Summary', en: 'Customers – summary' },
    pillar: 'partner',
    partnerOnly: true,
    api:    'getPartnerCustomersSummary',
    supports: ['kpi', 'summary'],
    defaultViz: 'kpi',
    fields: {
      total_customers:          { label: { de: 'Kunden gesamt',       en: 'Total customers' },    variant: 'primary',  icon: 'briefcase' },
      active_customers:         { label: { de: 'Aktiv',               en: 'Active' },              variant: 'teal',     icon: 'check-circle' },
      customers_with_workflows: { label: { de: 'Mit Workflows',       en: 'With workflows' },      variant: 'info',     icon: 'desktop' },
      total_confirmed_matches:  { label: { de: 'Bestätigte Matches',  en: 'Confirmed matches' },   variant: 'critical', icon: 'flag' },
      total_pending_matches:    { label: { de: 'Offene Matches',      en: 'Pending matches' },     variant: 'warning',  icon: 'clock' },
    },
    kpi: (res, field) => Number(unwrap(res)?.[field] ?? 0),
    link: '/partner/customers/list',
  },

  'customers-top10': {
    label:  { de: 'Top 10 gefährdete Kunden', en: 'Top 10 customers at risk' },
    pillar: 'partner',
    partnerOnly: true,
    api:    'getPartnerCustomersSummary',
    supports: ['table'],
    defaultViz: 'table',
    columns: () => [
      { field: '_rank',                 label: { de: '#',         en: '#' } },
      { field: 'name',                  label: { de: 'Kunde',     en: 'Customer' }, type: 'link', linkTo: r => `/partner/customers/${r.id}` },
      { field: 'risk_severity',         label: { de: 'Risiko',    en: 'Risk' },     type: 'severity' },
      { field: 'confirmed_match_count', label: { de: 'Bestätigt', en: 'Confirmed' }, align: 'right' },
      { field: 'pending_match_count',   label: { de: 'Offen',     en: 'Pending' },   align: 'right' },
    ],
    rows: (res) => (unwrap(res)?.top_at_risk ?? []).map((row, i) => ({ ...row, _rank: i + 1 })),
    link: '/partner/customers/list',
  },

  'customers-sync': {
    label:  { de: 'Kunden – Sync-Status', en: 'Customers – sync status' },
    pillar: 'partner',
    partnerOnly: true,
    api:    'getPartnerCustomersSyncOverview',
    supports: ['table', 'kpi', 'summary'],
    defaultViz: 'table',
    fields: {
      error:           { label: { de: 'Sync-Fehler',        en: 'Sync errors' },       variant: 'critical', icon: 'times-circle' },
      stale:           { label: { de: 'Sync überfällig',    en: 'Sync overdue' },      variant: 'warning',  icon: 'clock' },
      ok:              { label: { de: 'Kunden synchron',    en: 'Customers in sync' }, variant: 'success',  icon: 'check-circle' },
      unconfigured:    { label: { de: 'Ohne Detection-Set', en: 'No detection set' },  variant: 'info',     icon: 'inbox' },
      pending_matches: { label: { de: 'Offene Matches',     en: 'Pending matches' },   variant: 'warning',  icon: 'flag' },
    },
    kpi: (res, field) => Number(unwrap(res)?.totals?.[field] ?? 0),
    columns: () => [
      { field: 'name',                label: { de: 'Kunde',        en: 'Customer' },  type: 'link', linkTo: r => `/partner/customers/${r.id}` },
      { field: 'health',              label: { de: 'Status',       en: 'Status' },    type: 'severity', labelField: '_health_label' },
      { field: '_rules',              label: { de: 'Regeln',       en: 'Rules' },     align: 'right' },
      { field: 'pending_match_count', label: { de: 'Offen',        en: 'Pending' },   align: 'right' },
      { field: 'last_synced_at',      label: { de: 'Letzter Sync', en: 'Last sync' }, type: 'datetime' },
    ],
    rows(res, cfg, ctx) {
      const HEALTH_LABELS = {
        ok:    { de: 'OK',          en: 'OK' },
        error: { de: 'Fehler',      en: 'Error' },
        stale: { de: 'Überfällig',  en: 'Overdue' },
        none:  { de: 'Kein Set',    en: 'No set' },
      }
      return (unwrap(res)?.customers ?? []).map(c => ({
        ...c,
        _health_label: wl(HEALTH_LABELS[c.health], ctx?.lang) || c.health,
        _rules: `${c.synced_rules}/${c.total_rules}`,
      }))
    },
    link: '/partner/customers/list',
  },

  'customer-alarms': {
    label:  { de: 'Kunden-Alarme', en: 'Customer alarms' },
    pillar: 'partner',
    partnerOnly: true,
    api:    'getPartnerCustomerEvents',
    // `customer` ist eine eigene Param-Konvention (Kunden-Auswahl im Configurator);
    // `limit` steuert die Anzahl der gezeigten Alarme.
    params: { limit: true, customer: true },
    supports: ['table', 'feed'],
    defaultViz: 'table',
    apiParams: (cfg) => ({
      page: 1,
      limit: cfg.limit ?? 10,
      ...(cfg.customer_id ? { customer_id: cfg.customer_id } : {}),
    }),
    columns: () => [
      { field: 'received_at',   label: { de: 'Wann',        en: 'When' },     type: 'datetime' },
      { field: 'customer_name', label: { de: 'Kunde',       en: 'Customer' }, type: 'link', linkTo: r => `/partner/customers/${r.customer_id}` },
      { field: '_alarm',        label: { de: 'Alarm',       en: 'Alarm' } },
      { field: '_severity',     label: { de: 'Schweregrad', en: 'Severity' }, type: 'severity' },
    ],
    rows(res) {
      const list = unwrap(res)              // unwrap → res.data.data = Event-Array
      return (Array.isArray(list) ? list : []).map(r => ({
        id:            r.id,
        customer_id:   r.customer_id,
        customer_name: r.customer_name ?? '—',
        received_at:   r.received_at,
        _alarm:        r.parsed?.title || r.parsed?.alertName || '—',
        _severity:     r.parsed?.severity ?? 'none',
      }))
    },
    feed(res) {
      const list = unwrap(res)
      return (Array.isArray(list) ? list : []).map(r => ({
        title:  r.parsed?.title || r.parsed?.alertName || (r.customer_name ?? '—'),
        ts:     r.received_at,
        action: r.customer_name ?? '',
        link:   `/partner/customers/${r.customer_id}`,
      }))
    },
    link: '/partner/customers/list',
  },
}

export const sourceMeta = (key) => WIDGET_SOURCES[key] ?? null

// Quellen, die einen Viz-Typ unterstützen (gefiltert nach Partner-Sichtbarkeit)
export function sourcesForViz(viz, { isPartner = false } = {}) {
  return Object.entries(WIDGET_SOURCES)
    .filter(([, s]) => s.supports.includes(viz) && (!s.partnerOnly || isPartner))
    .map(([key, s]) => ({ key, ...s }))
}

export const PILLAR_LABELS = {
  threatnews:  { de: 'Threat News',         en: 'Threat News' },
  threatintel: { de: 'Threat Intelligence', en: 'Threat Intelligence' },
  detection:   { de: 'Detection',           en: 'Detection' },
  partner:     { de: 'Kunden',              en: 'Customers' },
}

// ── Widget-Katalog: Hauptgruppen (Ebene 1) + Vorlagen (Ebene 2) ──────────────
// Array-Reihenfolge = Default-Priorität (Kunde zuerst). Die User-Sortierung
// wird im Katalog-Drawer via localStorage überlagert.
// Eintrag in `templates`: String = Source-Key mit defaultViz, oder Objekt
// { source, viz?, config?, label? } für Varianten derselben Quelle.
export const CATALOG_GROUPS = [
  {
    key: 'kunden', icon: 'pi pi-briefcase', partnerOnly: true,
    label: { de: 'Kunden', en: 'Customers' },
    templates: [
      'customer-alarms',
      { source: 'customer-alarms', viz: 'feed', label: { de: 'Kunden-Alarme – Feed', en: 'Customer alarms – feed' } },
      'customers-sync',
      { source: 'customers-sync', viz: 'summary', label: { de: 'Sync-Gesundheit – Überblick', en: 'Sync health – overview' },
        config: { fields: ['error', 'stale', 'ok', 'pending_matches'] } },
      { source: 'customers-sync', viz: 'kpi', label: { de: 'Offene Matches', en: 'Pending matches' },
        config: { field: 'pending_matches' } },
      { source: 'customers-sync', viz: 'kpi', label: { de: 'Kunden mit Sync-Fehlern', en: 'Customers with sync errors' },
        config: { field: 'error' } },
      'customers-top10',
      'customers-summary',
    ],
  },
  {
    key: 'ransomware', icon: 'pi pi-megaphone',
    label: { de: 'Ransomware-Posts', en: 'Ransomware posts' },
    templates: ['posts-latest', 'posts-summary', 'posts-stats', 'posts-countries-map'],
  },
  {
    key: 'detection', icon: 'pi pi-desktop',
    label: { de: 'Detection & SIEM', en: 'Detection & SIEM' },
    templates: ['workflows-summary', 'workflows-stats', 'workflow-changes', 'streams-stats', 'siem-activity'],
  },
  {
    key: 'actors', icon: 'pi pi-users',
    label: { de: 'Threat Actors', en: 'Threat actors' },
    templates: ['groups-summary', 'groups-stats', 'groups-hacked-7d', 'hackwatch-activity'],
  },
  {
    key: 'news', icon: 'pi pi-globe',
    label: { de: 'Threat News', en: 'Threat news' },
    templates: ['news-latest', 'news-stats'],
  },
  {
    key: 'intel', icon: 'pi pi-shield',
    label: { de: 'IOCs & Tools', en: 'IOCs & tools' },
    templates: ['iocs-stats', 'tools-stats'],
  },
]

export const DAYS_OPTIONS  = [7, 30, 90, 365]
export const LIMIT_OPTIONS = [5, 10, 15, 25]

// ── Default-Config je Viz/Quelle ─────────────────────────────────────────────
export function defaultConfig(viz, sourceKey) {
  const src = sourceMeta(sourceKey)
  const cfg = {}
  if (!src) return cfg
  if (src.params?.days)     cfg.days  = 30
  if (src.params?.limit)    cfg.limit = 10
  if (src.params?.customer) cfg.customer_id = null   // null = alle Kunden
  if (src.dimensions)    cfg.dimension = Object.keys(src.dimensions)[0]
  if (viz === 'kpi' && src.fields)     cfg.field  = Object.keys(src.fields)[0]
  if (viz === 'summary' && src.fields) cfg.fields = Object.keys(src.fields)
  return cfg
}

// ── Default-Größen je Viz (24-Spalten-Grid, 40px-Zeilen) ─────────────────────
export const VIZ_DEFAULT_SIZE = {
  kpi:           { w: 4,  h: 5 },
  summary:       { w: 8,  h: 6 },
  table:         { w: 12, h: 11 },
  donut:         { w: 6,  h: 8 },
  'donut-table': { w: 8,  h: 8 },
  line:          { w: 12, h: 9 },
  map:           { w: 24, h: 14 },
  feed:          { w: 8,  h: 11 },
  info:          { w: 24, h: 9 },
}
